import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './sow-page.scss';

const API = process.env.REACT_APP_RYZ_SERVER;
function authHeaders() {
  const token = sessionStorage.getItem('token');
  return { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } };
}

function fmt(amount) {
  return Number(amount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
function fmtDate(d) {
  if (!d) return '—';
  return String(d).slice(0, 10);
}
function toDateInput(val) {
  if (!val) return '';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    const y = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${y}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  }
  return '';
}
function toMoney(val) {
  if (val == null || val === '') return '';
  const n = Number(String(val).replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : '';
}
function errDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join('; ');
  return JSON.stringify(d);
}
function calcPct(amount, total) {
  const a = Number(amount);
  const t = Number(total);
  if (!t || !Number.isFinite(a)) return '';
  return `${((a / t) * 100).toFixed(1)}%`;
}
function cleanRole(role) {
  const r = String(role || '').trim();
  if (!r || r === '-' || r === '—' || r === '–' || r === '...') return '';
  return r;
}

const STATUS_BADGE = {
  NOT_INVOICED: 'badge-pending',
  INVOICED: 'badge-invoiced',
  PAID: 'badge-paid',
  DRAFT: 'badge-draft',
  ACTIVE: 'badge-active',
  ARCHIVED: 'badge-archived',
};

const EMPTY_FORM = {
  client_id: '',
  project_name: '',
  sow_title: '',
  effective_date: '',
  term_start: '',
  term_end: '',
  total_amount: '',
  currency: 'USD',
  payment_terms_days: 15,
  notes: '',
  installments: [],
};

export default function SowPage() {
  const [tab, setTab] = useState('due');
  const [clients, setClients] = useState([]);
  const [dueInstallments, setDueInstallments] = useState([]);
  const [sows, setSows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [uploading, setUploading] = useState(false);
  const [parsedFields, setParsedFields] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [projectOptions, setProjectOptions] = useState([]);
  const fileRef = useRef();
  const [filterClient, setFilterClient] = useState('');
  const [editingSowId, setEditingSowId] = useState(null);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }

  useEffect(() => {
    axios.get(`${API}/list_clients`, authHeaders())
      .then(r => setClients(r.data || []))
      .catch(() => {});
  }, []);

  // Load project options from open_roles + clients.projects when client changes
  useEffect(() => {
    if (!form.client_id) {
      setProjectOptions([]);
      return;
    }
    const client = clients.find(c => String(c.id) === String(form.client_id));
    const fromClient = (client?.projects || '')
      .split(',')
      .map(p => p.trim())
      .filter(Boolean);

    Promise.all([
      axios.get(`${API}/find_project_names/${form.client_id}`, authHeaders()).catch(() => ({ data: [] })),
      axios.get(`${API}/get_project_names/${form.client_id}`, authHeaders()).catch(() => ({ data: '' })),
    ]).then(([rolesRes, clientsRes]) => {
      const fromRoles = Array.isArray(rolesRes.data) ? rolesRes.data : [];
      const fromGet = String(clientsRes.data || '')
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);
      const merged = Array.from(new Set([
        ...fromRoles.map(p => String(p).trim()).filter(Boolean),
        ...fromGet,
        ...fromClient,
        ...(form.project_name ? [String(form.project_name).trim()] : []),
      ].filter(Boolean))).sort((a, b) => a.localeCompare(b));
      setProjectOptions(merged);
    });
  }, [form.client_id, form.project_name, clients]);

  useEffect(() => {
    if (tab === 'due') loadDue();
    if (tab === 'list') loadSows();
  }, [tab, filterClient]); // eslint-disable-line

  function loadDue() {
    setLoading(true); setError('');
    const qs = filterClient ? `?client_id=${filterClient}` : '';
    axios.get(`${API}/sow/due_installments/list${qs}`, authHeaders())
      .then(r => setDueInstallments(r.data || []))
      .catch(e => setError(errDetail(e, 'Failed to load due installments')))
      .finally(() => setLoading(false));
  }

  function loadSows({ soft = false } = {}) {
    if (!soft) { setLoading(true); setError(''); }
    const qs = filterClient ? `?client_id=${filterClient}` : '';
    axios.get(`${API}/sow/list${qs}`, authHeaders())
      .then(r => setSows(r.data || []))
      .catch(e => { if (!soft) setError(errDetail(e, 'Failed to load SOWs')); })
      .finally(() => { if (!soft) setLoading(false); });
  }

  async function handlePdfUpload(e) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true); setError(''); setParsedFields(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/sow/upload_parse`, fd, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      const p = r.data.parsed || {};
      setParsedFields(p);
      const total = toMoney(p.total_amount);
      const installments = (p.installments || []).map((inst, i) => ({
        installment_no: inst.installment_no || (i + 1),
        label: inst.label || `Installment ${i + 1}`,
        amount: toMoney(inst.amount),
        due_date: toDateInput(inst.due_date),
        notes: '',
      }));
      setForm(prev => ({
        ...prev,
        sow_title: p.sow_title || prev.sow_title || '',
        effective_date: toDateInput(p.effective_date) || '',
        term_start: toDateInput(p.term_start) || '',
        term_end: toDateInput(p.term_end) || '',
        total_amount: total === '' ? '' : total,
        currency: p.currency || 'USD',
        payment_terms_days: p.payment_terms_days || 15,
        installments,
      }));
      if (!installments.length) {
        setError('Extracted SOW header fields, but no installments were found — add them manually.');
      }
    } catch (err) {
      setError(errDetail(err, 'PDF parse failed'));
    }
    setUploading(false);
  }

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function setInstField(idx, k, v) {
    setForm(f => {
      const insts = [...f.installments];
      insts[idx] = { ...insts[idx], [k]: v };
      return { ...f, installments: insts };
    });
  }

  function addInstallment() {
    setForm(f => ({
      ...f,
      installments: [...f.installments, {
        installment_no: f.installments.length + 1,
        label: `Installment ${f.installments.length + 1}`,
        amount: '',
        due_date: '',
        notes: '',
      }],
    }));
  }

  function removeInstallment(idx) {
    const inst = form.installments[idx];
    if (inst && ((inst.status && inst.status !== 'NOT_INVOICED') || inst.client_invoice_id)) {
      setError(`Cannot remove installment "${inst.label || idx + 1}" — already invoiced`);
      return;
    }
    setForm(f => ({
      ...f,
      installments: f.installments
        .filter((_, i) => i !== idx)
        .map((row, i) => ({ ...row, installment_no: i + 1 })),
    }));
  }

  function startEditSow(sow) {
    setEditingSowId(sow.id);
    setParsedFields(null);
    setError('');
    setForm({
      client_id: String(sow.client_id || ''),
      project_name: sow.project_name || '',
      sow_title: sow.sow_title || '',
      effective_date: toDateInput(sow.effective_date),
      term_start: toDateInput(sow.term_start),
      term_end: toDateInput(sow.term_end),
      total_amount: sow.total_amount ?? '',
      currency: sow.currency || 'USD',
      payment_terms_days: sow.payment_terms_days || 15,
      notes: sow.notes || '',
      status: sow.status || 'ACTIVE',
      installments: (sow.installments || []).map((inst, i) => ({
        id: inst.id,
        installment_no: inst.installment_no || (i + 1),
        label: inst.label || `Installment ${i + 1}`,
        amount: inst.amount ?? '',
        due_date: toDateInput(inst.due_date),
        notes: inst.notes || '',
        status: inst.status,
        client_invoice_id: inst.client_invoice_id,
      })),
    });
    setTab('upload');
  }

  function cancelEditSow() {
    setEditingSowId(null);
    setForm({ ...EMPTY_FORM });
    setParsedFields(null);
    setError('');
    setTab('list');
  }

  async function editSowById(sowId) {
    try {
      setLoading(true);
      setError('');
      const r = await axios.get(`${API}/sow/${sowId}`, authHeaders());
      startEditSow(r.data);
    } catch (err) {
      setError(errDetail(err, 'Failed to load SOW for edit'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSowSave(e) {
    e.preventDefault();
    if (!form.client_id) { setError('Client is required'); return; }
    if (!(form.project_name || form.sow_title || '').trim()) { setError('Project Name (or SOW Title) is required'); return; }
    if (!form.installments.length) { setError('Add at least one installment'); return; }
    for (let i = 0; i < form.installments.length; i++) {
      const inst = form.installments[i];
      if (inst.amount === '' || inst.amount == null) {
        setError(`Installment ${i + 1}: amount is required`);
        return;
      }
      if (!inst.due_date) {
        setError(`Installment ${i + 1}: due date is required`);
        return;
      }
    }
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        client_id: parseInt(form.client_id, 10),
        project_name: (form.project_name || form.sow_title || '').trim(),
        total_amount: parseFloat(form.total_amount) || 0,
        status: form.status || 'ACTIVE',
        installments: form.installments.map((inst, i) => ({
          ...(inst.id ? { id: inst.id } : {}),
          installment_no: i + 1,
          label: inst.label || `Installment ${i + 1}`,
          amount: parseFloat(inst.amount),
          due_date: inst.due_date,
          notes: inst.notes || null,
        })),
      };
      if (editingSowId) {
        await axios.put(`${API}/sow/${editingSowId}`, payload, authHeaders());
        showToast('SOW updated');
      } else {
        await axios.post(`${API}/sow/new`, payload, authHeaders());
        showToast('SOW created successfully');
      }
      setEditingSowId(null);
      setForm({ ...EMPTY_FORM });
      setParsedFields(null);
      setTab('list');
    } catch (err) {
      setError(errDetail(err, editingSowId ? 'Failed to update SOW' : 'Failed to save SOW'));
    }
    setLoading(false);
  }

  async function generateInvoice(sowId, installmentId) {
    try {
      const r = await axios.post(`${API}/sow/${sowId}/installments/${installmentId}/generate_invoice`, {}, authHeaders());
      if (r.data.status === 'already_invoiced') {
        showToast(`Already invoiced — Invoice #${r.data.client_invoice_id}`);
      } else {
        showToast(`Invoice #${r.data.client_invoice_id} created ✓`);
      }
      loadDue();
    } catch (err) {
      setError(errDetail(err, 'Failed to generate invoice'));
    }
  }

  return (
    <div className="sow-page">
      {toast && <div className="sow-toast">{toast}</div>}

      <div className="sow-header">
        <h1>Statements of Work</h1>
        <div className="sow-tabs">
          <button className={tab === 'due' ? 'active' : ''} onClick={() => setTab('due')}>
            Due Installments {dueInstallments.length > 0 && <span className="sow-badge-count">{dueInstallments.length}</span>}
          </button>
          <button className={tab === 'list' ? 'active' : ''} onClick={() => setTab('list')}>All SOWs</button>
          <button
            className={tab === 'upload' ? 'active' : ''}
            onClick={() => {
              if (editingSowId) {
                setEditingSowId(null);
                setForm({ ...EMPTY_FORM });
                setParsedFields(null);
              }
              setTab('upload');
            }}
          >
            {editingSowId ? 'Edit SOW' : '+ New SOW'}
          </button>
        </div>
      </div>

      {(tab === 'due' || tab === 'list') && (
        <div className="sow-filters">
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="filter-input">
            <option value="">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {error && <div className="sow-error">{error}</div>}
      {loading && <div className="sow-loading">Loading…</div>}

      {tab === 'due' && !loading && (
        <div className="sow-section">
          {dueInstallments.length === 0
            ? <p className="sow-empty">No installments due. 🎉</p>
            : (
              <table className="sow-table">
                <thead>
                  <tr>
                    <th>Client</th><th>SOW Title</th><th>Project</th>
                    <th>Installment</th><th>Amount</th>
                    <th>Due</th><th>Days Overdue</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dueInstallments.map(inst => (
                    <tr key={inst.installment_id} className={inst.days_overdue > 0 ? 'row-overdue' : ''}>
                      <td>{inst.client_name || inst.client_id}</td>
                      <td><strong>{inst.sow_title || '—'}</strong></td>
                      <td>{inst.project_name}</td>
                      <td>{inst.label || `#${inst.installment_no}`}</td>
                      <td>{fmt(inst.amount)}</td>
                      <td>{fmtDate(inst.due_date)}</td>
                      <td>{inst.days_overdue > 0 ? <span className="overdue-text">{inst.days_overdue}d</span> : 'Due today'}</td>
                      <td className="sow-actions-cell">
                        <button
                          type="button"
                          className="btn-secondary sow-edit-btn"
                          onClick={() => editSowById(inst.sow_id)}
                        >
                          Edit
                        </button>
                        <button className="btn-invoice" onClick={() => generateInvoice(inst.sow_id, inst.installment_id)}>
                          Create Invoice
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      {tab === 'list' && !loading && (
        <div className="sow-section">
          {sows.length === 0
            ? <p className="sow-empty">No SOWs yet. Create one with + New SOW.</p>
            : sows.map(sow => (
                <SowCard
                  key={sow.id}
                  sow={sow}
                  clients={clients}
                  onRefresh={() => loadSows({ soft: true })}
                  onDeleted={() => loadSows()}
                  onEdit={startEditSow}
                  showToast={showToast}
                />
              ))}
        </div>
      )}

      {tab === 'upload' && (
        <div className="sow-section sow-form-section">
          {!editingSowId && (
          <div className="sow-card">
            <h3>Step 1 — Upload SOW PDF <span className="optional">(optional — auto-fills fields below)</span></h3>
            <form onSubmit={handlePdfUpload} className="sow-upload-form">
              <input ref={fileRef} type="file" accept=".pdf" />
              <button type="submit" disabled={uploading} className="btn-secondary">
                {uploading ? 'Extracting…' : 'Extract Fields'}
              </button>
            </form>
            {parsedFields && <p className="sow-parsed-note">✓ Fields extracted — review and confirm below.</p>}
          </div>
          )}

          <form onSubmit={handleSowSave} className="sow-card sow-form">
            <h3>{editingSowId ? `Edit SOW #${editingSowId}` : 'Step 2 — SOW Details'}</h3>
            <div className="sow-form-grid">
              <label>Client *
                <select
                  value={form.client_id}
                  onChange={e => setForm(f => ({ ...f, client_id: e.target.value, project_name: '' }))}
                  required
                >
                  <option value="">Select client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label>Project Name *
                <select
                  value={form.project_name}
                  onChange={e => setField('project_name', e.target.value)}
                  required
                  disabled={!form.client_id}
                >
                  <option value="">{form.client_id ? 'Select project…' : 'Select client first…'}</option>
                  {projectOptions.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {form.client_id && projectOptions.length === 0 && (
                  <span className="optional">No projects found for this client — add one under Open Roles first.</span>
                )}
              </label>
              <label>SOW Title
                <input value={form.sow_title} onChange={e => setField('sow_title', e.target.value)} />
              </label>
              <label>Effective Date
                <input type="date" value={form.effective_date} onChange={e => setField('effective_date', e.target.value)} />
              </label>
              <label>Term Start
                <input type="date" value={form.term_start} onChange={e => setField('term_start', e.target.value)} />
              </label>
              <label>Term End
                <input type="date" value={form.term_end} onChange={e => setField('term_end', e.target.value)} />
              </label>
              <label>Total Amount ($)
                <input type="number" step="0.01" value={form.total_amount} onChange={e => setField('total_amount', e.target.value)} />
              </label>
              <label>Currency
                <input value={form.currency} onChange={e => setField('currency', e.target.value)} maxLength={3} />
              </label>
              <label>Payment Terms (days)
                <input type="number" value={form.payment_terms_days} onChange={e => setField('payment_terms_days', parseInt(e.target.value) || 15)} />
              </label>
            </div>
            <label className="label-full">Notes
              <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} />
            </label>

            <div className="sow-installments-section">
              <div className="sow-installments-header">
                <h4>Installments</h4>
                <button type="button" className="btn-add" onClick={addInstallment}>+ Add Installment</button>
              </div>
              {form.installments.map((inst, idx) => (
                <div key={idx} className="sow-installment-row">
                  <span className="inst-no">{idx + 1}</span>
                  <input placeholder="Label" value={inst.label} onChange={e => setInstField(idx, 'label', e.target.value)} />
                  <input type="number" step="0.01" placeholder="Amount $" value={inst.amount} onChange={e => setInstField(idx, 'amount', e.target.value)} />
                  <span className="inst-pct" title="Calculated from amount / total">
                    {calcPct(inst.amount, form.total_amount) || '—'}
                  </span>
                  <input type="date" value={inst.due_date} onChange={e => setInstField(idx, 'due_date', e.target.value)} title="Due date" />
                  <button
                    type="button"
                    className="btn-remove"
                    title={(inst.status && inst.status !== 'NOT_INVOICED') || inst.client_invoice_id ? 'Already invoiced' : 'Remove'}
                    disabled={!!((inst.status && inst.status !== 'NOT_INVOICED') || inst.client_invoice_id)}
                    onClick={() => removeInstallment(idx)}
                  >✕</button>
                </div>
              ))}
              {form.installments.length === 0 && <p className="sow-empty-small">No installments yet — extract from PDF or add manually.</p>}
            </div>

            <div className="sow-form-actions">
              {editingSowId && (
                <button type="button" className="btn-secondary" onClick={cancelEditSow} disabled={loading}>
                  Cancel
                </button>
              )}
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Saving…' : (editingSowId ? 'Update SOW' : 'Save SOW')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SowCard({ sow, clients, onRefresh, onDeleted, onEdit, showToast }) {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [assignments, setAssignments] = useState(sow.assignments || []);
  const [assignForm, setAssignForm] = useState({ candidate_id: '', percent: '100', role_label: '' });
  const [assignBusy, setAssignBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setAssignments(sow.assignments || []);
  }, [sow.assignments]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/find_candidate_by_client/${sow.client_id}`, authHeaders())
      .then(r => setCandidates(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCandidates([]));
  }, [open, sow.client_id]);

  async function handleGenerate(installmentId) {
    setGenerating(installmentId);
    try {
      const r = await axios.post(
        `${API}/sow/${sow.id}/installments/${installmentId}/generate_invoice`, {},
        authHeaders()
      );
      if (r.data.status === 'already_invoiced') {
        showToast(`Already invoiced — Invoice #${r.data.client_invoice_id}`);
      } else {
        showToast(`Invoice #${r.data.client_invoice_id} created ✓`);
      }
      onRefresh();
    } catch (err) {
      showToast(errDetail(err, 'Failed'));
    }
    setGenerating(null);
  }

  async function handleAddAssignment(e) {
    e.preventDefault();
    if (!assignForm.candidate_id) return;
    setAssignBusy(true);
    try {
      const selected = candidates.find(c => String(c.id) === String(assignForm.candidate_id));
      const roleLabel = cleanRole(assignForm.role_label) || cleanRole(selected?.role) || null;
      const r = await axios.post(
        `${API}/sow/${sow.id}/assignments`,
        {
          candidate_id: parseInt(assignForm.candidate_id, 10),
          percent: parseFloat(assignForm.percent) || 100,
          role_label: roleLabel,
        },
        authHeaders()
      );
      setAssignments(r.data.assignment || []);
      // Keep panel open — only reset person picker so more can be added
      setAssignForm({ candidate_id: '', percent: '100', role_label: '' });
      showToast('Person assigned to SOW');
      onRefresh();
    } catch (err) {
      showToast(errDetail(err, 'Failed to assign'));
    }
    setAssignBusy(false);
  }

  async function handleDeleteSow(e) {
    e.stopPropagation();
    const label = sow.sow_title || sow.project_name || `SOW #${sow.id}`;
    if (!window.confirm(`Delete "${label}" and all installments/people assignments? Linked SOW invoices will also be removed.`)) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/sow/${sow.id}`, authHeaders());
      showToast('SOW deleted');
      if (onDeleted) onDeleted();
      else onRefresh();
    } catch (err) {
      showToast(errDetail(err, 'Failed to delete SOW'));
    }
    setDeleting(false);
  }

  function handleCandidatePick(candidateId) {
    const selected = candidates.find(c => String(c.id) === String(candidateId));
    setAssignForm(f => ({
      ...f,
      candidate_id: candidateId,
      role_label: cleanRole(selected?.role) || f.role_label,
    }));
  }

  async function handleRemoveAssignment(assignmentId) {
    if (!window.confirm('Remove this person from the SOW?')) return;
    setAssignBusy(true);
    try {
      const r = await axios.delete(`${API}/sow/assignments/${assignmentId}`, authHeaders());
      setAssignments(r.data.assignments || []);
      showToast('Assignment removed');
      onRefresh();
    } catch (err) {
      showToast(errDetail(err, 'Failed to remove'));
    }
    setAssignBusy(false);
  }

  async function handlePercentBlur(assignmentId, value) {
    const pct = parseFloat(value);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      showToast('Percent must be between 0 and 100');
      onRefresh();
      return;
    }
    try {
      const r = await axios.put(
        `${API}/sow/assignments/${assignmentId}`,
        { percent: pct },
        authHeaders()
      );
      setAssignments(r.data.assignment || []);
    } catch (err) {
      showToast(errDetail(err, 'Failed to update %'));
      onRefresh();
    }
  }

  const invoicedTotal = sow.installments
    .filter(i => i.status === 'INVOICED' || i.status === 'PAID')
    .reduce((s, i) => s + Number(i.amount || 0), 0);
  const paidTotal = sow.installments
    .filter(i => i.status === 'PAID')
    .reduce((s, i) => s + Number(i.amount || 0), 0);
  const assignedIds = new Set(assignments.map(a => a.candidate_id));
  const availableCandidates = candidates.filter(c => !assignedIds.has(c.id));
  const clientName = (clients || []).find(c => c.id === sow.client_id)?.name;
  const sowTitle = (sow.sow_title || '').trim() || sow.project_name || `SOW #${sow.id}`;

  return (
    <div className={`sow-card ${open ? 'sow-card--open' : ''}`}>
      <div className="sow-card-header" onClick={() => setOpen(o => !o)}>
        <div className="sow-card-title">
          <span className={`badge ${STATUS_BADGE[sow.status] || 'badge-draft'}`}>{sow.status}</span>
          <strong title="SOW Title">{sowTitle}</strong>
          {sow.project_name && sow.project_name !== sowTitle && (
            <span className="sow-project-tag">Project: {sow.project_name}</span>
          )}
          {assignments.length > 0 && (
            <span className="sow-project-tag">{assignments.length} people</span>
          )}
        </div>
        <div className="sow-card-meta">
          <span>{fmt(sow.total_amount)}</span>
          <span className="sow-term">{fmtDate(sow.term_start)} → {fmtDate(sow.term_end)}</span>
          <span className="sow-progress">{fmt(invoicedTotal)} invoiced</span>
          {paidTotal > 0 && (
            <span className="sow-progress sow-progress--paid">{fmt(paidTotal)} paid</span>
          )}
          <button
            type="button"
            className="btn-secondary sow-edit-btn"
            title="Edit SOW"
            onClick={(e) => { e.stopPropagation(); onEdit && onEdit(sow); }}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn-remove sow-delete-btn"
            disabled={deleting}
            title="Delete SOW"
            onClick={handleDeleteSow}
          >
            {deleting ? '…' : 'Delete'}
          </button>
          <span className="sow-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div className="sow-card-body">
          <h4 style={{ marginBottom: 10 }}>Installments</h4>
          <table className="sow-table">
            <thead>
              <tr>
                <th>#</th><th>Label</th><th>Amount</th><th>%</th>
                <th>Due Date</th><th>Invoice Due</th><th>Status</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sow.installments.map(inst => (
                <tr key={inst.id}>
                  <td>{inst.installment_no}</td>
                  <td>{inst.label || '—'}</td>
                  <td>{fmt(inst.amount)}</td>
                  <td>{inst.percent_of_total ? `${inst.percent_of_total}%` : '—'}</td>
                  <td>{fmtDate(inst.due_date)}</td>
                  <td>{fmtDate(inst.invoice_due_date)}</td>
                  <td><span className={`badge ${STATUS_BADGE[inst.status] || 'badge-pending'}`}>{inst.status}</span></td>
                  <td>
                    {inst.status === 'NOT_INVOICED' ? (
                      <button
                        className="btn-invoice btn-sm"
                        disabled={generating === inst.id}
                        onClick={() => handleGenerate(inst.id)}
                      >
                        {generating === inst.id ? '…' : 'Invoice'}
                      </button>
                    ) : (
                      <span className="link-muted">
                        {inst.client_invoice_id ? `Invoice #${inst.client_invoice_id}` : '—'}
                        {inst.invoice_status ? ` · ${inst.invoice_status}` : ''}
                        {inst.status === 'PAID' ? ' · PAID' : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="sow-assign-section">
            <div className="sow-installments-header">
              <h4>People on this SOW {clientName ? `(${clientName})` : ''}</h4>
            </div>
            <p className="sow-empty-small" style={{ marginBottom: 10 }}>
              Assigned people are excluded from timesheet client billing. Use % for P&amp;L cost split.
            </p>
            <table className="sow-table">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>%</th><th></th>
                </tr>
              </thead>
              <tbody>
                {assignments.length === 0 && (
                  <tr><td colSpan={4} className="sow-empty-small">No people assigned yet.</td></tr>
                )}
                {assignments.map(a => (
                  <tr key={a.id}>
                    <td>{a.candidate_name || `#${a.candidate_id}`}</td>
                    <td>{cleanRole(a.role_label) || '—'}</td>
                    <td>
                      <input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        defaultValue={a.percent}
                        className="sow-pct-input"
                        onBlur={e => handlePercentBlur(a.id, e.target.value)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-remove"
                        disabled={assignBusy}
                        onClick={() => handleRemoveAssignment(a.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <form className="sow-assign-form" onSubmit={handleAddAssignment}>
              <select
                value={assignForm.candidate_id}
                onChange={e => handleCandidatePick(e.target.value)}
                required
              >
                <option value="">Select person…</option>
                {availableCandidates.map(c => {
                  const role = cleanRole(c.role);
                  return (
                    <option key={c.id} value={c.id}>
                      {role ? `${c.name} — ${role}` : c.name}
                    </option>
                  );
                })}
              </select>
              <input
                type="number"
                min="0.01"
                max="100"
                step="0.01"
                placeholder="%"
                value={assignForm.percent}
                onChange={e => setAssignForm(f => ({ ...f, percent: e.target.value }))}
                style={{ maxWidth: 90 }}
              />
              <input
                placeholder="Role label (optional)"
                value={assignForm.role_label}
                onChange={e => setAssignForm(f => ({ ...f, role_label: e.target.value }))}
              />
              <button type="submit" className="btn-secondary" disabled={assignBusy || !assignForm.candidate_id}>
                {assignBusy ? '…' : '+ Assign'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
